/* =========================================================
   Affiche SPORTS COLLECTIFS — le terrain, le match, la saison, les stats.
   API : SNCoTerrain.render(data, opts) -> string SVG
     data : { mode, sport, titre, kicker, club, lieu, date, joueur, equipe,
              sport  : 'basket' | 'handball' | 'volley' | 'rugby'
              mode 'terrain'  → s1..s3 (« valeur|libellé ») ; sinon les cotes officielles
              mode 'match'    → adversaire, score, scoreAdv, resultat
              mode 'saison'   → l1..l8 (« Libellé|valeur »)
              mode 'stats'    → t1..t6 (« Valeur|Libellé »)
              mode 'palmares' → p1..p8 (« 12 juin|Chartres|68-54|V ») }
     opts : { palette }

   La géométrie de l'aire de jeu est PARAMÉTRÉE (map AIRES) : la même fonction
   dessine le parquet de basket, le terrain de handball, celui de volley et la
   pelouse de rugby, chacun avec ses tracés réglementaires. Ajouter un sport
   collectif = une entrée dans AIRES.

   Auto-suffisant (aucun fetch, aucune image) → re-render serveur direct.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    craie:   { label:'Craie',   bg:'#F7F4ED', ink:'#0B2D5B', muted:'#8B94A3', accent:'#FF6B2C', line:'#E3DDD0', card:'#FFFFFF', aire:'#C9793C', deep:'#A85F2C' },
    parquet: { label:'Parquet', imposeAire: true, bg:'#2A1B10', ink:'#F4EFE6', muted:'#B39A80', accent:'#FF8C42', line:'#4A3320', card:'#3A2616', aire:'#C9793C', deep:'#8A5326' },
    pelouse: { label:'Pelouse', imposeAire: true, bg:'#12362B', ink:'#F4F1E8', muted:'#8FB3A5', accent:'#FF6B2C', line:'#1C5544', card:'#164536', aire:'#1B6B4A', deep:'#0B271F' },
    nuit:    { label:'Nuit',    bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F', aire:'#123E70', deep:'#082444' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const num = s => { const m = String(s == null ? '' : s).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  /* Cotes réelles. `dessin(g)` reçoit un contexte normalisé où x et y sont des
     fractions du terrain (0-1) : la géométrie reste juste quelle que soit la
     taille à laquelle on la dessine. */
  const AIRES = {
    basket: {
      ratio: 28 / 15, teinte: '#C9793C',
      cotes: [['28 m','Longueur'], ['15 m','Largeur'], ['3,05 m','Panier']],
      /* COTES FIBA, en mètres, converties au tracé. Terrain 28 × 15.
         Un mètre vaut W/15 : comme H = W × 28/15, l'échelle est la MÊME dans
         les deux directions, donc les cercles restent ronds. Les valeurs
         ci-dessous sont les cotes officielles, pas des approximations.
           rond central            rayon 1,80
           raquette                4,90 de large × 5,80 depuis la ligne de fond
           cercle de lancer franc  rayon 1,80, centré sur la ligne des LF
           panier                  centre à 1,575 · ANNEAU rayon 0,225
           panneau                 1,80 de large, à 1,20 de la ligne de fond
           ligne à 3 points        rayon 6,75 depuis le panier, parties droites
                                   à 0,90 des lignes de côté
           zone de non-charge      rayon 1,25 depuis le panier
           marques de rebond       1,75 · 2,60 · 3,45 · 4,30 */
      dessin: (c) => {
        const { X, Y, W, H, L, sw } = c;
        const fin = sw * 0.75, m = W / 15;          /* un mètre, en unités SVG */
        const fx = mx => X + W / 2 + mx * m;        /* mètres depuis l'axe long */
        let g = `<line x1="${X}" y1="${(Y+H/2).toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${(Y+H/2).toFixed(1)}" stroke="${L}" stroke-width="${sw}"/>`;
        g += `<circle cx="${(X+W/2).toFixed(1)}" cy="${(Y+H/2).toFixed(1)}" r="${(1.80*m).toFixed(1)}" fill="none" stroke="${L}" stroke-width="${sw}"/>`;
        for (const s of [0, 1]) {
          const y0 = s ? Y + H : Y, sg = s ? -1 : 1;
          const fy = my => y0 + sg * my * m;        /* mètres depuis la ligne de fond */
          const xg = fx(-2.45), xd = fx(2.45), yLF = fy(5.80);

          g += `<rect x="${xg.toFixed(1)}" y="${Math.min(y0, yLF).toFixed(1)}" width="${(4.90*m).toFixed(1)}" height="${(5.80*m).toFixed(1)}" fill="none" stroke="${L}" stroke-width="${sw}"/>`;
          /* EMPLACEMENTS DE REBOND — chaîne de cotes officielle depuis le bord
             intérieur de la ligne de fond : 1,75 puis emplacement 0,85, puis
             ZONE NEUTRE 0,40, puis 0,85, puis 0,85. Soit CINQ marques à
             1,75 · 2,60 · 3,00 · 3,85 · 4,70, et non quatre au pas régulier.
             Les traits débordent de 0,10 seulement, pas de 0,40. */
          /* Les POSITIONS sont exactes ; le DÉBORD, lui, a un plancher. À
             l'échelle de l'affiche, 0,10 m ne fait que 1,8 unité : les marques
             disparaîtraient purement et simplement, alors qu'un joueur s'attend
             à les voir. On garde donc 0,10 dès que c'est lisible, sinon le
             minimum qui le reste. Compromis assumé, et le seul du tracé. */
          const deb = Math.max(0.10 * m, 4.5);
          for (const d of [1.75, 2.60, 3.00, 3.85, 4.70]) {
            const yy = fy(d);
            for (const [xx, dir] of [[xg, -1], [xd, 1]])
              g += `<line x1="${xx.toFixed(1)}" y1="${yy.toFixed(1)}" x2="${(xx + dir*deb).toFixed(1)}" y2="${yy.toFixed(1)}" stroke="${L}" stroke-width="${sw}"/>`;
          }
          /* La zone neutre est un bloc PLEIN de 2,60 à 3,00 : c'est elle qui
             distingue le premier emplacement des deux suivants. */
          for (const dir of [-1, 1]) {
            const xx = dir < 0 ? xg : xd;
            g += `<rect x="${Math.min(xx, xx + dir*deb).toFixed(1)}" y="${Math.min(fy(2.60), fy(3.00)).toFixed(1)}" width="${deb.toFixed(1)}" height="${(0.40*m).toFixed(1)}" fill="${L}"/>`;
          }
          /* Cercle de lancer franc : la FIBA ne marque QUE le demi-cercle côté
             terrain. Le demi-cercle pointillé dans la raquette est une
             convention NBA/NCAA, absente des diagrammes officiels. */
          const rLF = 1.80 * m;
          g += `<path d="M ${fx(-1.80).toFixed(1)} ${yLF.toFixed(1)} A ${rLF.toFixed(1)} ${rLF.toFixed(1)} 0 0 ${s ? 1 : 0} ${fx(1.80).toFixed(1)} ${yLF.toFixed(1)}" fill="none" stroke="${L}" stroke-width="${sw}"/>`;

          /* LIGNE À TROIS POINTS d'un seul tracé : les parties droites à 0,90
             des lignes de côté rejoignent l'arc EXACTEMENT au point de tangence,
             calculé et non estimé. dx = 7,50 − 0,90 = 6,60 ;
             dy = √(6,75² − 6,60²) = 1,4151 → tangence à 2,9901 de la ligne de fond. */
          const r3 = 6.75 * m, dx3 = 7.50 - 0.90;
          const yT = 1.575 + Math.sqrt(6.75 * 6.75 - dx3 * dx3);
          g += `<path d="M ${fx(-dx3).toFixed(1)} ${y0.toFixed(1)} L ${fx(-dx3).toFixed(1)} ${fy(yT).toFixed(1)} A ${r3.toFixed(1)} ${r3.toFixed(1)} 0 0 ${s ? 1 : 0} ${fx(dx3).toFixed(1)} ${fy(yT).toFixed(1)} L ${fx(dx3).toFixed(1)} ${y0.toFixed(1)}" fill="none" stroke="${L}" stroke-width="${sw}"/>`;

          /* panneau, anneau, puis zone de non-charge refermée sur le panneau */
          g += `<line x1="${fx(-0.90).toFixed(1)}" y1="${fy(1.20).toFixed(1)}" x2="${fx(0.90).toFixed(1)}" y2="${fy(1.20).toFixed(1)}" stroke="${L}" stroke-width="${sw*1.3}"/>`
            + `<circle cx="${(X+W/2).toFixed(1)}" cy="${fy(1.575).toFixed(1)}" r="${(0.225*m).toFixed(1)}" fill="none" stroke="${L}" stroke-width="${fin}"/>`;
          /* Zone de non-charge : rayon 1,30 (et non 1,25), refermée par deux
             droites de 0,375 qui s'arrêtent à 1,20 de la ligne de fond. */
          const rNC = 1.30 * m;
          g += `<path d="M ${fx(-1.30).toFixed(1)} ${fy(1.20).toFixed(1)} L ${fx(-1.30).toFixed(1)} ${fy(1.575).toFixed(1)} A ${rNC.toFixed(1)} ${rNC.toFixed(1)} 0 0 ${s ? 1 : 0} ${fx(1.30).toFixed(1)} ${fy(1.575).toFixed(1)} L ${fx(1.30).toFixed(1)} ${fy(1.20).toFixed(1)}" fill="none" stroke="${L}" stroke-width="${sw}"/>`;
        }
        return g;
      },
    },
    handball: {
      ratio: 40 / 20, teinte: '#2E6DA4',
      cotes: [['40 m','Longueur'], ['20 m','Largeur'], ['6 m','Zone']],
      dessin: (c) => {
        const { X, Y, W, H, L, sw } = c;
        let g = `<line x1="${X}" y1="${(Y+H/2).toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${(Y+H/2).toFixed(1)}" stroke="${L}" stroke-width="${sw}"/>`;
        for (const s of [0, 1]) {
          const y0 = s ? Y + H : Y, sg = s ? -1 : 1;
          /* zone des 6 m : deux quarts de cercle reliés par un segment droit
             devant le but, c'est ce qui rend un terrain de hand reconnaissable */
          const r6 = W * 0.30, r9 = W * 0.45, bx = W * 0.15;
          for (const [r, dash] of [[r6, ''], [r9, ` stroke-dasharray="${(W*0.035).toFixed(1)} ${(W*0.030).toFixed(1)}"`]]) {
            /* Chaque quart de cercle est centré sur un POTEAU et doit bomber
               VERS LE TERRAIN. Le sens de balayage inversé donnait un
               entonnoir creusé vers le but — l'inverse d'une zone de hand. */
            g += `<path d="M ${(X + W/2 - bx - r).toFixed(1)} ${y0.toFixed(1)}`
              + ` A ${r.toFixed(1)} ${r.toFixed(1)} 0 0 ${s ? 1 : 0} ${(X + W/2 - bx).toFixed(1)} ${(y0 + sg*r).toFixed(1)}`
              + ` L ${(X + W/2 + bx).toFixed(1)} ${(y0 + sg*r).toFixed(1)}`
              + ` A ${r.toFixed(1)} ${r.toFixed(1)} 0 0 ${s ? 1 : 0} ${(X + W/2 + bx + r).toFixed(1)} ${y0.toFixed(1)}" fill="none" stroke="${L}" stroke-width="${sw}"${dash}/>`;
          }
          /* but : 3 m de large */
          g += `<rect x="${(X + W/2 - W*0.075).toFixed(1)}" y="${(s ? y0 : y0 - H*0.018).toFixed(1)}" width="${(W*0.15).toFixed(1)}" height="${(H*0.018).toFixed(1)}" fill="${L}" opacity="0.9"/>`;
          /* LES PETITS TRAITS DU HAND : la ligne des 7 mètres (1 m de long) et
             la ligne de limitation du gardien à 4 m (15 cm). Ce sont eux qu'un
             joueur cherche des yeux sur un plan de terrain. */
          const y7 = y0 + sg * H * (7/40), y4 = y0 + sg * H * (4/40);
          g += `<line x1="${(X + W/2 - W*0.025).toFixed(1)}" y1="${y7.toFixed(1)}" x2="${(X + W/2 + W*0.025).toFixed(1)}" y2="${y7.toFixed(1)}" stroke="${L}" stroke-width="${sw}"/>`
            + `<line x1="${(X + W/2 - W*0.0125).toFixed(1)}" y1="${y4.toFixed(1)}" x2="${(X + W/2 + W*0.0125).toFixed(1)}" y2="${y4.toFixed(1)}" stroke="${L}" stroke-width="${sw*0.8}"/>`;
        }
        /* marques de la zone de changement : 4,50 m de part et d'autre du
           milieu, débordant de 15 cm sur l'extérieur de la touche */
        for (const s of [-1, 1]) {
          const yy = Y + H/2 + s * H * (4.5/40);
          for (const [xx, d] of [[X, -1], [X + W, 1]])
            g += `<line x1="${xx.toFixed(1)}" y1="${yy.toFixed(1)}" x2="${(xx + d*W*0.035).toFixed(1)}" y2="${yy.toFixed(1)}" stroke="${L}" stroke-width="${sw}"/>`;
        }
        return g;
      },
    },
    volley: {
      ratio: 18 / 9, teinte: '#2E6DA4',
      cotes: [['18 m','Longueur'], ['9 m','Largeur'], ['2,43 m','Filet']],
      dessin: (c) => {
        const { X, Y, W, H, L, sw, p } = c;
        let g = '';
        /* lignes d'attaque à 3 m du filet, de part et d'autre */
        for (const s of [-1, 1]) {
          const yy = Y + H/2 + s * H * (3/18);
          g += `<line x1="${X}" y1="${yy.toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${yy.toFixed(1)}" stroke="${L}" stroke-width="${sw}"/>`;
          /* prolongement pointillé de la ligne d'attaque à l'extérieur du
             terrain : cinq petits traits de 15 cm, la marque du volley */
          for (const [xx, d] of [[X, -1], [X + W, 1]])
            for (let k = 0; k < 5; k++)
              g += `<line x1="${(xx + d*(W*0.030 + k*W*0.048)).toFixed(1)}" y1="${yy.toFixed(1)}" x2="${(xx + d*(W*0.060 + k*W*0.048)).toFixed(1)}" y2="${yy.toFixed(1)}" stroke="${L}" stroke-width="${sw*0.8}"/>`;
        }
        /* marques de la zone de service : deux petits traits derrière chaque
           ligne de fond, dans le prolongement des lignes de côté */
        for (const s of [0, 1]) {
          const y0 = s ? Y + H : Y, sg = s ? 1 : -1;
          for (const xx of [X, X + W])
            g += `<line x1="${xx.toFixed(1)}" y1="${y0.toFixed(1)}" x2="${xx.toFixed(1)}" y2="${(y0 + sg*H*0.026).toFixed(1)}" stroke="${L}" stroke-width="${sw}"/>`;
        }
        /* le filet, avec ses poteaux qui débordent */
        const dy = W * 0.055, nx = X - W*0.06, nw = W * 1.12;
        g += `<rect x="${nx.toFixed(1)}" y="${(Y + H/2 - dy/2).toFixed(1)}" width="${nw.toFixed(1)}" height="${dy.toFixed(1)}" fill="${p.deep}"/>`;
        for (let i = 1; i < 16; i++) g += `<line x1="${(nx + nw*i/16).toFixed(1)}" y1="${(Y + H/2 - dy/2).toFixed(1)}" x2="${(nx + nw*i/16).toFixed(1)}" y2="${(Y + H/2 + dy/2).toFixed(1)}" stroke="${L}" stroke-width="1" opacity="0.28"/>`;
        g += `<rect x="${nx.toFixed(1)}" y="${(Y + H/2 - dy/2).toFixed(1)}" width="${nw.toFixed(1)}" height="${(dy*0.24).toFixed(1)}" fill="${L}" opacity="0.95"/>`;
        return g;
      },
    },
    rugby: {
      ratio: 120 / 70, teinte: '#2E7D4F',
      cotes: [['100 m','Longueur'], ['70 m','Largeur'], ['22 m','Ligne des 22']],
      dessin: (c) => {
        const { X, Y, W, H, L, sw } = c;
        /* le terrain fait 100 m entre les lignes d'essai, plus deux en-buts de
           10 m : les fractions ci-dessous sont donc sur 120 m au total */
        let g = `<line x1="${X}" y1="${(Y+H/2).toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${(Y+H/2).toFixed(1)}" stroke="${L}" stroke-width="${sw*1.2}"/>`;
        for (const [f, dash] of [[10/120, ''], [32/120, ''], [50/120, ` stroke-dasharray="${(W*0.05).toFixed(1)} ${(W*0.04).toFixed(1)}"`]]) {
          for (const s of [0, 1]) {
            const yy = s ? Y + H - H*f : Y + H*f;
            g += `<line x1="${X}" y1="${yy.toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${yy.toFixed(1)}" stroke="${L}" stroke-width="${sw}"${dash} opacity="${f === 50/120 ? 0.75 : 1}"/>`;
          }
        }
        /* LES POINTILLÉS DU RUGBY : les lignes des 5 m et des 15 m, qui courent
           sur toute la longueur en petits traits. Ce sont elles, plus que les
           lignes transversales, qui font reconnaître une pelouse de rugby. */
        const yEssai0 = Y + H*(10/120), yEssai1 = Y + H - H*(10/120);
        for (const f of [5/70, 15/70]) {
          for (const d of [f, 1 - f]) {
            const xx = X + W * d;
            g += `<line x1="${xx.toFixed(1)}" y1="${yEssai0.toFixed(1)}" x2="${xx.toFixed(1)}" y2="${yEssai1.toFixed(1)}" stroke="${L}" stroke-width="${sw*0.7}" stroke-dasharray="${(H*0.012).toFixed(1)} ${(H*0.026).toFixed(1)}" opacity="0.85"/>`;
          }
        }
        /* Poteaux : VUS DU DESSUS ce sont deux plots sur la ligne d'essai,
           écartés de 5,60 m. Dessiner un H serait une vue de face plaquée sur
           un plan — la faute que ferait remarquer n'importe quel joueur. */
        for (const s of [0, 1]) {
          const yy = s ? yEssai1 : yEssai0;
          const ecart = W * 0.040;
          for (const d of [-1, 1])
            g += `<circle cx="${(X + W/2 + d*ecart).toFixed(1)}" cy="${yy.toFixed(1)}" r="${(sw*1.5).toFixed(1)}" fill="${L}"/>`;
        }
        return g;
      },
    },
    hockey: {
      ratio: 60 / 30, teinte: '#DCE9F2', rayonFrac: 8.5 / 30,
      cotes: [['60 m','Longueur'], ['30 m','Largeur'], ['15 m','Zone neutre']],
      /* COTES IIHF, rulebook 2025/26. Patinoire 60 × 30, coins de rayon 8,50.
         Un mètre vaut W/30 : comme H = W × 2, l'échelle est la MÊME dans les
         deux directions, donc les cercles restent ronds.

         LES LIGNES BLEUES SONT À 22,50 m DE LA BANDE, ce qui donne une zone
         neutre de 15,00 m — exactement un quart de la longueur. Le 22,86 m
         (zone neutre 14,28) que répètent Wikipédia et à peu près tous les
         schémas en ligne est la règle IIHF 2014-2022 : elle est PÉRIMÉE.

         La ligne de but est à 4,00 m de la bande, mesurée depuis la partie
         PLATE du fond. À cette hauteur elle coupe déjà les arrondis : elle
         s'arrête à 8,5 − √(8,5² − 4,5²) = 1,289 m de chaque bande latérale,
         et ne mesure donc que 27,42 m au lieu de 30. */
      dessin: (c) => {
        const { X, Y, W, H, L } = c;
        const m = W / 30;
        const fin = Math.max(1, 0.05 * m), gros = Math.max(2, 0.30 * m);
        const cx = X + W / 2;
        const ROUGE = '#D0342C', BLEU = '#2E5FAF';
        const fy = d => Y + d * m;                 /* mètres depuis la bande du haut */
        let g = '';

        /* Ligne médiane et lignes bleues, sur toute la largeur. */
        g += `<line x1="${X}" y1="${fy(30).toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${fy(30).toFixed(1)}" stroke="${ROUGE}" stroke-width="${gros.toFixed(1)}"/>`;
        for (const d of [22.5, 37.5])
          g += `<line x1="${X}" y1="${fy(d).toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${fy(d).toFixed(1)}" stroke="${BLEU}" stroke-width="${gros.toFixed(1)}"/>`;

        /* Lignes de but, tronquées par les arrondis — pas d'un bord à l'autre. */
        const retrait = (8.5 - Math.sqrt(8.5 * 8.5 - 4.5 * 4.5)) * m;
        for (const d of [4, 56])
          g += `<line x1="${(X + retrait).toFixed(1)}" y1="${fy(d).toFixed(1)}" x2="${(X + W - retrait).toFixed(1)}" y2="${fy(d).toFixed(1)}" stroke="${ROUGE}" stroke-width="${fin.toFixed(1)}"/>`;

        /* Les buts et leur zone : demi-cercle de 1,80 m ouvert vers le centre. */
        for (const s of [0, 1]) {
          const yb = fy(s ? 56 : 4), sg = s ? -1 : 1;
          g += `<path d="M ${(cx - 1.8*m).toFixed(1)} ${yb.toFixed(1)} A ${(1.8*m).toFixed(1)} ${(1.8*m).toFixed(1)} 0 0 ${s ? 0 : 1} ${(cx + 1.8*m).toFixed(1)} ${yb.toFixed(1)}"`
            + ` fill="${BLEU}" fill-opacity="0.22" stroke="${ROUGE}" stroke-width="${fin.toFixed(1)}"/>`
            + `<rect x="${(cx - 0.915*m).toFixed(1)}" y="${(yb - (s ? 0 : 1.15*m)).toFixed(1)}" width="${(1.83*m).toFixed(1)}" height="${(1.15*m).toFixed(1)}" fill="none" stroke="${ROUGE}" stroke-width="${fin.toFixed(1)}"/>`;
        }

        /* CINQ CERCLES seulement : le central en bleu, quatre en zone de but
           en rouge. Les quatre points de zone neutre n'ont PAS de cercle —
           c'est l'erreur la plus fréquente des schémas. */
        g += `<circle cx="${cx.toFixed(1)}" cy="${fy(30).toFixed(1)}" r="${(4.5*m).toFixed(1)}" fill="none" stroke="${BLEU}" stroke-width="${fin.toFixed(1)}"/>`
          + `<circle cx="${cx.toFixed(1)}" cy="${fy(30).toFixed(1)}" r="${(0.15*m).toFixed(1)}" fill="${BLEU}"/>`;
        for (const d of [10, 50]) for (const sg of [-1, 1]) {
          const px = cx + sg * 7 * m;
          g += `<circle cx="${px.toFixed(1)}" cy="${fy(d).toFixed(1)}" r="${(4.5*m).toFixed(1)}" fill="none" stroke="${ROUGE}" stroke-width="${fin.toFixed(1)}"/>`
            + `<circle cx="${px.toFixed(1)}" cy="${fy(d).toFixed(1)}" r="${(0.30*m).toFixed(1)}" fill="${ROUGE}"/>`;
        }
        /* Les quatre points de zone neutre : à 1,50 m de la ligne bleue. */
        for (const d of [24, 36]) for (const sg of [-1, 1])
          g += `<circle cx="${(cx + sg * 7 * m).toFixed(1)}" cy="${fy(d).toFixed(1)}" r="${(0.30*m).toFixed(1)}" fill="${ROUGE}"/>`;
        return g;
      },
    },
    nfl: {
      ratio: 120 / (160 / 3), teinte: '#2E7D4F',
      cotes: [['100 yd','Terrain'], ['10 yd','En-but'], ['160 pi','Largeur']],
      /* COTES NFL. Terrain 120 yards sur 160 pieds (53⅓ yards), en-buts de
         10 yards compris. Un yard vaut W/53,333.

         LES HASH MARKS SONT LE PIÈGE. En NFL les deux rangées sont écartées de
         18 pi 6 po — c'est un ÉCART, pas une distance à la touche : chaque
         rangée est donc à 70 pi 9 po (23,58 yd) de sa ligne de touche. La NCAA
         les écarte de 40 pi et le lycée de 53 pi 4 po. Confondre l'écart et la
         distance à la touche est l'erreur la plus répandue.

         Le motif, lui, est LE MÊME aux trois niveaux : sur chaque ligne de
         5 yards un trait parallèle à la touche, bissecté par elle, plus quatre
         tirets perpendiculaires entre deux lignes — les repères d'un yard. */
      dessin: (c) => {
        const { X, Y, W, H, L } = c;
        const yd = W / (160 / 3);
        const fin = Math.max(1, 0.11 * yd), fort = Math.max(1.6, 0.16 * yd);
        const fy = d => Y + d * yd;                /* yards depuis le fond du haut */
        const hash = 70.75 / 3;                    /* 70 pi 9 po, en yards */
        let g = '';

        /* Lignes tous les 5 yards, entre les deux lignes de but. */
        for (let d = 15; d <= 105; d += 5)
          g += `<line x1="${X}" y1="${fy(d).toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${fy(d).toFixed(1)}" stroke="${L}" stroke-width="${fin.toFixed(1)}"/>`;
        /* Lignes de but et lignes de fond, plus épaisses. */
        for (const d of [0, 10, 110, 120])
          g += `<line x1="${X}" y1="${fy(d).toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${fy(d).toFixed(1)}" stroke="${L}" stroke-width="${fort.toFixed(1)}"/>`;

        /* Les hash marks. Motif identique aux trois niveaux ; seul l'écart des
           deux rangées change, et c'est celui de la NFL qui est employé ici. */
        for (const sg of [-1, 1]) {
          const hx = sg < 0 ? X + hash * yd : X + W - hash * yd;
          for (let d = 11; d <= 109; d++) {
            if (d % 5 === 0) {
              /* trait parallèle à la touche, bissecté par la ligne */
              g += `<line x1="${hx.toFixed(1)}" y1="${fy(d - 0.36).toFixed(1)}" x2="${hx.toFixed(1)}" y2="${fy(d + 0.36).toFixed(1)}" stroke="${L}" stroke-width="${fin.toFixed(1)}"/>`;
            } else {
              /* repère d'un yard, perpendiculaire */
              g += `<line x1="${(hx - 0.34 * yd).toFixed(1)}" y1="${fy(d).toFixed(1)}" x2="${(hx + 0.34 * yd).toFixed(1)}" y2="${fy(d).toFixed(1)}" stroke="${L}" stroke-width="${(fin * 0.8).toFixed(1)}"/>`;
            }
          }
        }

        /* Les numéros, tous les 10 yards, à 4 yards de chaque touche. Ils
           montent de 10 à 50 puis redescendent : c'est la distance à la ligne
           de but la plus proche, pas une numérotation continue. */
        /* Les numéros mesurent 6 pieds de haut sur un vrai terrain, soit 2 yards.
           Tracés à cette taille exacte ils deviennent illisibles sur une affiche —
           on les porte à 2,8 yards. C est le seul écart de ce tracé, et il porte
           sur la SEULE chose qui doive rester lisible de loin. */
        const tailleN = Math.max(10, 2.8 * yd);
        for (let d = 20; d <= 100; d += 10) {
          const n = String(50 - Math.abs(60 - d));
          for (const x of [X + 4.6 * yd, X + W - 4.6 * yd])
            g += `<text x="${x.toFixed(1)}" y="${(fy(d) + tailleN * 0.34).toFixed(1)}" font-family="Oswald" font-size="${tailleN.toFixed(1)}" font-weight="700" fill="${L}" text-anchor="middle" opacity="0.9">${n}</text>`;
          /* La flèche pointe vers la ligne de but la plus proche : vers le haut
             dans la moitié haute, vers le bas dans la moitié basse. */
          if (d !== 60) { const sg = d < 60 ? -1 : 1, fl = tailleN * 0.30;
            for (const x of [X + 4.6 * yd, X + W - 4.6 * yd])
              g += `<path d="M ${x.toFixed(1)} ${(fy(d) + sg * tailleN * 0.66).toFixed(1)} l ${fl.toFixed(1)} ${(-sg*fl).toFixed(1)} l ${(-2*fl).toFixed(1)} 0 z" fill="${L}" opacity="0.9"/>`; }
        }

        /* Les poteaux, dans le plan de la LIGNE DE FOND et non de la ligne de
           but — ils y ont été reculés en 1974. Vus de dessus, deux montants
           écartés de 18 pi 6 po. */
        for (const d of [0, 120]) for (const sg of [-1, 1])
          g += `<circle cx="${(X + W/2 + sg * (18.5 / 6) * yd).toFixed(1)}" cy="${fy(d).toFixed(1)}" r="${Math.max(1.8, 0.30 * yd).toFixed(1)}" fill="${L}"/>`;
        return g;
      },
    },
  };

  /* Encombrement réservé à l'aire de jeu : la largeur est DÉDUITE du ratio,
     sinon un terrain de rugby (1,71) et un de handball (2,00) dessinés à la
     même largeur n'occuperaient pas la même hauteur. */
  const BOITE = { lng: 520, largMax: 330 };

  function aire(sportId, cx, cy, p) {
    const a = AIRES[sportId] || AIRES.basket;
    const W = Math.min(BOITE.largMax, BOITE.lng / a.ratio), H = W * a.ratio;
    const X = cx - W/2, Y = cy - H/2;
    const L = '#FFFFFF', sw = Math.max(2, W * 0.008);
    /* Chaque sport a sa surface : parquet au basket, résine bleue en salle,
       pelouse au rugby. Les deux palettes thématiques (parquet, pelouse)
       imposent la leur ; les neutres laissent parler le sport. */
    const surface = p.imposeAire ? p.aire : (a.teinte || p.aire);
    /* COINS ARRONDIS, en fraction de la largeur. Une patinoire de hockey n'est
       pas un rectangle : ses quatre coins décrivent un arc, et un tracé à
       angles droits ne se lit plus comme de la glace. Les aires qui ne
       déclarent rien gardent leurs angles vifs — le réglage est neutre pour
       les quatre sports déjà en ligne. */
    const rx = a.rayonFrac ? (W * a.rayonFrac).toFixed(1) : 0;
    let g = `<rect x="${X.toFixed(1)}" y="${Y.toFixed(1)}" width="${W.toFixed(1)}" height="${H.toFixed(1)}" rx="${rx}" fill="${surface}"/>`;
    g += a.dessin({ X, Y, W, H, L, sw, p });
    /* bordure tracée EN DEDANS : sur fond clair, la moitié extérieure d'un
       trait blanc centré disparaîtrait dans le fond */
    g += `<rect x="${(X + sw/2).toFixed(1)}" y="${(Y + sw/2).toFixed(1)}" width="${(W - sw).toFixed(1)}" height="${(H - sw).toFixed(1)}"`
      + (rx ? ` rx="${(W * a.rayonFrac - sw / 2).toFixed(1)}"` : '') + ` fill="none" stroke="${L}" stroke-width="${sw}"/>`;
    return { g, X, Y, W, H };
  }

  function stats(p, items, y) {
    const it = items.filter(x => String(x[0] || '').trim());
    if (!it.length) return '';
    const x0 = 54, w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }
  function troisValeurs(p, data, y, defaut) {
    const perso = [1, 2, 3].map(i => {
      const raw = String(data['s' + i] || '').trim(); if (!raw) return null;
      const [v, l] = raw.split('|').map(s => (s || '').trim());
      return v ? [v, l || ''] : null;
    });
    return stats(p, perso.some(Boolean) ? perso.filter(Boolean) : (defaut || []), y);
  }

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.craie;
    const mode = String(data.mode || 'terrain').toLowerCase();
    const sportId = AIRES[data.sport] ? data.sport : 'basket';
    const aireDef = AIRES[sportId];
    const cx = PW / 2;

    const DEF = { terrain: 'LE TERRAIN', match: 'LE MATCH', saison: 'NOTRE SAISON', stats: 'MES STATS', palmares: 'NOTRE PALMARÈS' };
    const titre = up(data.titre) || DEF[mode] || 'LE TERRAIN';
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || up(data.club) || 'SPORT COLLECTIF';
    const sous = [up(data.club), up(data.lieu)].filter(Boolean).join(' · ');

    let corps = '', barre = '', pied = 0;

    if (mode === 'terrain') {
      corps = aire(sportId, cx, 600, p).g;
      barre = troisValeurs(p, data, 968, aireDef.cotes);
      pied = 1030;

    } else if (mode === 'match') {
      const a = aire(sportId, cx, 552, p);
      corps = a.g;
      const mien = num(data.score), adv = num(data.scoreAdv);
      const gagne = mien != null && adv != null && mien > adv;
      corps += `<rect x="${MX}" y="838" width="${PW-2*MX}" height="122" rx="14" fill="${p.ink}"/>`
        + T(cx, 878, 13, p.accent, esc(up(data.resultat) || (gagne ? 'VICTOIRE' : '')), { w:700, ls:8 })
        + `<text x="${cx}" y="932" text-anchor="middle" font-family="Oswald" font-weight="700" fill="${p.bg}"><tspan font-size="54">${esc(up(data.score) || '—')}</tspan><tspan font-size="26" fill="${p.muted}" dx="12">–</tspan><tspan font-size="54" dx="12">${esc(up(data.scoreAdv) || '—')}</tspan></text>`;
      pied = 1024;

    } else if (mode === 'stats') {
      /* Six blocs valeur / libellé : le vocabulaire change d'un sport à
         l'autre (rebonds au basket, arrêts au hand, aces au volley), donc le
         libellé est saisi par le client et non imposé par le moteur. */
      const bl = [];
      for (let i = 1; i <= 6; i++) {
        const raw = String(data['t' + i] || '').trim(); if (!raw) continue;
        const [v, l] = raw.split('|').map(s => (s || '').trim());
        if (v && l) bl.push([v, l]);
      }
      if (bl.length) {
        const cols = bl.length <= 4 ? 2 : 3, rangs = Math.ceil(bl.length / cols);
        const bw = (PW - 2*MX - (cols-1)*16) / cols, bh = Math.min(180, 560 / rangs);
        bl.forEach((b, i) => {
          const c = i % cols, r = Math.floor(i / cols);
          const x = MX + c * (bw + 16), y = 340 + r * (bh + 16);
          corps += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${bh.toFixed(1)}" rx="16" fill="${p.card}"/>`
            + T(x + bw/2, y + bh*0.60, fit(b[0], bw - 28, 62, 0.56, 0, 24), p.ink, esc(b[0]), { f:'Oswald', w:700 })
            + T(x + bw/2, y + bh*0.82, 13, p.muted, esc(up(b[1])), { w:600, ls:2 });
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : LA VALEUR, UNE BARRE VERTICALE, SON LIBELLÉ', { w:600, ls:1.5 });
      barre = troisValeurs(p, data, 976, null);
      pied = 1032;

    } else if (mode === 'palmares') {
      const l = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['p' + i] || '').trim(); if (!raw) continue;
        const c = raw.split('|').map(s => (s || '').trim());
        if (c[1]) l.push(c);
      }
      if (l.length) {
        const zone = 620, pas = Math.min(102, zone / l.length), y0 = 340 + (zone - pas*l.length) / 2;
        l.forEach((r, i) => {
          const y = y0 + i * pas, v = /^v/i.test(r[3] || '');
          corps += (r[0] ? T(MX, y - 12, 13, p.muted, esc(up(r[0])), { w:600, ls:2, a:'start' }) : '')
            + T(MX, y + 16, fit(r[1], 400, 26, 0.60, 0, 15), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'start' })
            + (r[2] ? T(PW - MX - 62, y + 16, 30, v ? p.accent : p.muted, esc(r[2]), { f:'Oswald', w:700, a:'end' }) : '')
            + (r[3] ? T(PW - MX, y + 14, 20, v ? p.accent : p.muted, esc(up(r[3])), { f:'Oswald', w:700, a:'end' }) : '');
          if (i < l.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 7).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 7).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : DATE, ADVERSAIRE, SCORE, V OU D', { w:600, ls:2 });
      pied = 1000;

    } else { /* saison */
      const l = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['l' + i] || '').trim(); if (!raw) continue;
        const [lab, v] = raw.split('|').map(s => (s || '').trim());
        if (lab && v) l.push([lab, v]);
      }
      if (l.length) {
        const zone = 600, pas = Math.min(96, zone / l.length), y0 = 348 + (zone - pas*l.length) / 2 - 40;
        l.forEach((r, i) => {
          const y = y0 + i * pas, dernier = i === l.length - 1;
          corps += T(MX, y, 17, p.muted, esc(up(r[0])), { w:600, ls:2, a:'start' })
            + T(PW - MX, y + 8, fit(r[1], 420, 44, 0.58, 0, 20), dernier ? p.accent : p.ink, esc(r[1]), { f:'Oswald', w:700, a:'end' });
          if (!dernier) corps += `<line x1="${MX}" y1="${(y + pas/2 - 14).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 14).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : LIBELLÉ, BARRE VERTICALE, VALEUR', { w:600, ls:2 });
      pied = 976;
    }

    const bas = [esc(up(data.joueur)), esc(up(data.equipe))].filter(Boolean).join(' · ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${corps}
${barre}
${pied && bas ? T(cx, pied, fit(bas, 690, 19, 0.60, 1, 12), p.ink, bas, { f:'Oswald', w:700, ls:1 }) : ''}
</svg>`;
  }

  window.SNCoTerrain = { render, PALETTES, AIRES };
})();
